import { useState, type ReactNode } from 'react';
import { Alert, Box, Button, Collapse } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';

/**
 * SASADM-0067 — de-emphasize a platform-auth surface (brand / OIDC / login) on an
 * EXTERNAL-auth product. `muted=false` → renders children UNCHANGED (the PLATFORM
 * path is untouched). `muted=true` → an info note explains the surface isn't used
 * under external auth, with the real content one click away (collapsed + greyed) so
 * it stays discoverable and recoverable. Purely visual: children stay mounted, so
 * any form state they hold is preserved and still submits.
 */
export function ExternalAuthMuted({
  muted,
  note,
  children,
}: {
  muted: boolean;
  note: string;
  children: ReactNode;
}) {
  const { t } = useCopy();
  const [revealed, setRevealed] = useState(false);
  if (!muted) return <>{children}</>;
  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="info"
        variant="outlined"
        action={
          <Button color="inherit" size="small" onClick={() => setRevealed((v) => !v)}>
            {revealed ? t('externalAuth.hide', 'Hide') : t('externalAuth.showAnyway', 'Show anyway')}
          </Button>
        }
      >
        {note}
      </Alert>
      <Collapse in={revealed}>
        <Box sx={{ opacity: 0.6, mt: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}
